@file:OptIn(kotlinx.serialization.ExperimentalSerializationApi::class)

package dev.lingvo.shared

import kotlinx.serialization.EncodeDefault
import kotlinx.serialization.Serializable

/** Where a capture came from. Defaults: `WEB` for a session, `CLI` for a token. */
@Serializable
enum class CaptureSource { AGENT, CLI, WEB, FASTLANE, STORE }

@Serializable
enum class CaptureReviewState { UNREVIEWED, APPROVED }

@Serializable
enum class CoverageState { MISSING, CURRENT, STALE }

@Serializable
enum class StaleReason { TEXT_CHANGED, TEXT_CHANGED_DURING_CAPTURE, CAPTURE_INVALIDATED, SLOT_INVALIDATED }

@Serializable
enum class TextChangeKind { CHANGED, ADDED, REMOVED }

/**
 * A string a slot's screen shows, by file path and key name.
 *
 * A slot that lists keys goes stale only when one of *those* changes; a slot that lists none goes
 * stale when any UI string of its locale (in the plan's watched files) changes. [deleted] is output
 * only: the key was removed, and the names are the ones it had when the slot listed it.
 */
@Serializable
data class SlotKeyRef(val file: String, val key: String, val deleted: Boolean = false)

@Serializable
data class ScreenshotSlotDto(
    val id: String,
    val platform: StorePlatform,
    val key: String,
    val position: Int,
    val title: String,
    val howTo: String = "",
    val devices: List<DeviceClass>,
    val accent: String? = null,
    val keys: List<SlotKeyRef> = emptyList(),
    /**
     * False: one capture in the source language serves every language (only the caption is
     * localized), so only the source locale is ever owed. True: one capture per language.
     */
    val localizedUi: Boolean = true,
    /** The caption string: file `store/<platform>/captions`, key name = [key]. Null if hard-deleted. */
    val captionKeyId: String? = null,
    val invalidatedAt: String? = null,
    val invalidatedReason: String? = null,
    val createdAt: String,
    val updatedAt: String,
    /** A declared synthetic fixture recipe in this platform's plan; never application records. */
    @EncodeDefault(EncodeDefault.Mode.NEVER)
    val demoDataProfile: String? = null,
)

/** [version] is also the ETag `"plan-<version>"`. [watchFiles]: paths whose strings can make a capture stale; empty = all. */
@Serializable
data class ScreenshotPlanDto(
    val platform: StorePlatform,
    val version: Long,
    val watchFiles: List<String> = emptyList(),
    val slots: List<ScreenshotSlotDto> = emptyList(),
    @EncodeDefault(EncodeDefault.Mode.NEVER)
    val demoDataProfiles: List<ScreenshotDemoDataProfileDto> = emptyList(),
)

@Serializable
data class SlotInput(
    val key: String,
    val title: String,
    val howTo: String = "",
    val devices: List<DeviceClass>,
    val accent: String? = null,
    val keys: List<SlotKeyRef> = emptyList(),
    val localizedUi: Boolean = true,
    /** Omission preserves an existing slot's recipe for older plan writers. */
    val demoDataProfile: String? = null,
    val clearDemoDataProfile: Boolean = false,
)

@Serializable
data class PutScreenshotPlanRequest(
    val slots: List<SlotInput>,
    val watchFiles: List<String>? = null,
    val prune: Boolean = false,
    /** Omission preserves recipes; an explicit empty list clears unreferenced recipes. */
    val demoDataProfiles: List<ScreenshotDemoDataProfileDto>? = null,
)

@Serializable
data class ShotPlanChangeDto(
    val plan: ScreenshotPlanDto,
    val created: List<String> = emptyList(),
    val updated: List<String> = emptyList(),
    val removed: List<String> = emptyList(),
    val unchanged: List<String> = emptyList(),
    val dryRun: Boolean = false,
)

@Serializable
data class CreateSlotRequest(
    val key: String,
    val title: String,
    val howTo: String = "",
    val devices: List<DeviceClass>,
    val accent: String? = null,
    val position: Int? = null,
    val keys: List<SlotKeyRef> = emptyList(),
    val localizedUi: Boolean = true,
    val demoDataProfile: String? = null,
)

/** The slot's key is immutable: it names the caption key and is the identity a plan file pushes by. */
@Serializable
data class UpdateSlotRequest(
    val title: String? = null,
    val howTo: String? = null,
    val devices: List<DeviceClass>? = null,
    val accent: String? = null,
    val clearAccent: Boolean = false,
    val keys: List<SlotKeyRef>? = null,
    val localizedUi: Boolean? = null,
    val demoDataProfile: String? = null,
    val clearDemoDataProfile: Boolean = false,
)

/** A permutation of every slot id, in the new order. */
@Serializable
data class ReorderSlotsRequest(val slotIds: List<String>, val planVersion: Long)

/** No locales and no devices: the whole slot is marked for retake. */
@Serializable
data class InvalidateCapturesRequest(
    val locales: List<String>? = null,
    val devices: List<DeviceClass>? = null,
    val reason: String,
)

@Serializable
data class InvalidateResultDto(val invalidated: Int)

@Serializable
data class ReviewCaptureRequest(val state: CaptureReviewState)

@Serializable
data class TextChangeSample(val keyName: String, val filePath: String, val change: TextChangeKind)

/** [sample]: at most five, ordered by file path then key name. */
@Serializable
data class StaleDetailDto(
    val reasons: List<StaleReason>,
    val changedKeys: Int = 0,
    val addedKeys: Int = 0,
    val removedKeys: Int = 0,
    val sample: List<TextChangeSample> = emptyList(),
)

/**
 * One stored capture. Never carries bytes: [imagePath] is the relative API path to fetch them.
 *
 * [storePushable]: meets the store rule with no alpha, so it could go out without a design.
 * [warnings]: facts that refuse nothing (an alpha channel in a capture). [supersededAt]: replaced by
 * a newer capture of the same cell and kept as the previous one. [blobMissing]: the row exists but
 * the file is not on the asset volume (a restore without it); it counts as missing in coverage.
 * [metadataStripped]: EXIF/XMP was removed byte-wise, so [sha256] is of the stored bytes and differs
 * from the uploaded file's.
 */
@Serializable
data class CaptureDto(
    val id: String,
    val slotId: String,
    val slotKey: String,
    val platform: StorePlatform,
    val deviceClass: DeviceClass,
    val locale: String,
    val format: ImageFormat,
    val width: Int,
    val height: Int,
    val byteSize: Int,
    val sha256: String,
    val hasAlpha: Boolean,
    val storePushable: Boolean,
    val storeIssues: List<ShotIssue> = emptyList(),
    val warnings: List<ShotIssue> = emptyList(),
    val source: CaptureSource,
    val reviewState: CaptureReviewState,
    val capturedAt: String,
    val uploadedBy: String,
    val state: CoverageState,
    val stale: StaleDetailDto? = null,
    val imagePath: String,
    val supersededAt: String? = null,
    val supersedesCaptureId: String? = null,
    val blobMissing: Boolean = false,
    val metadataStripped: Boolean = false,
)

@Serializable
data class CoverageLocaleDto(val locale: String, val storeLocale: String, val isSource: Boolean, val textFingerprint: String)

/** [reusesLocale]: the slot is not localized, and this cell shows the source locale's capture. */
@Serializable
data class CoverageCellDto(
    val locale: String,
    val deviceClass: DeviceClass,
    val state: CoverageState,
    val captureId: String? = null,
    val stale: StaleDetailDto? = null,
    val storePushable: Boolean = false,
    val reviewState: CaptureReviewState? = null,
    val blobMissing: Boolean = false,
    val reusesLocale: String? = null,
)

@Serializable
data class CoverageRowDto(val slotId: String, val slotKey: String, val title: String, val position: Int, val cells: List<CoverageCellDto>)

@Serializable
data class CoverageTotalsDto(val missing: Int, val stale: Int, val current: Int)

/** [orphans]: captures for a locale no longer in the project or a device no longer in its slot; counted in quota. */
@Serializable
data class CoverageDto(
    val platform: StorePlatform,
    val planVersion: Long,
    val locales: List<CoverageLocaleDto>,
    val rows: List<CoverageRowDto>,
    val totals: CoverageTotalsDto,
    val orphans: List<CaptureDto> = emptyList(),
    val quota: ScreenshotQuotaDto,
)

/** Apple: the exact [sizes]. Play: [sizes] empty and [rule] is the capture rule's sentence. */
@Serializable
data class WantedImageDto(
    val deviceClass: DeviceClass,
    val sizes: List<PixelSize>,
    val rule: String,
    val formats: List<ImageFormat>,
    val alphaAllowed: Boolean,
    val maxBytes: Int,
)

@Serializable
enum class RetakeReason { MISSING, STALE }

/**
 * One capture the agent owes.
 *
 * [uploadPath]: the PUT target relative to `/api/v1`, with `?basis=<basis>` appended. [basis] is the
 * fingerprint of the text this slot shows in this locale right now: upload against it, and a text
 * change that lands while the agent is capturing marks the new capture stale on arrival.
 * [ticketPath]: POST here (relative to `/api/v1`) for a single-use upload ticket instead.
 */
@Serializable
data class RetakeItemDto(
    val slotId: String,
    val slotKey: String,
    val title: String,
    val howTo: String,
    val position: Int,
    val deviceClass: DeviceClass,
    val reason: RetakeReason,
    val stale: StaleDetailDto? = null,
    val wanted: WantedImageDto,
    val uploadPath: String,
    val ticketPath: String,
    val basis: String,
    val replacesCaptureId: String? = null,
    @EncodeDefault(EncodeDefault.Mode.NEVER)
    val demoDataProfile: ScreenshotDemoDataProfileDto? = null,
)

@Serializable
data class RetakeLocaleGroupDto(val locale: String, val storeLocale: String, val textFingerprint: String, val items: List<RetakeItemDto>)

@Serializable
data class RetakeQueueDto(
    val platform: StorePlatform,
    val planVersion: Long,
    val generatedAt: String,
    val groups: List<RetakeLocaleGroupDto>,
    val total: Int,
)

/**
 * A request for a single-use upload ticket: the hosted MCP's way to let an agent upload a file it
 * holds without the bytes ever passing through a tool call.
 *
 * The ticket is bound to exactly these bytes ([sha256] of the file and its [byteSize]) and to one
 * cell. [ifMatch] and [createOnly] are the upload's preconditions (`If-Match` / `If-None-Match: *`).
 */
@Serializable
data class UploadTicketRequest(
    val sha256: String,
    val byteSize: Int,
    val source: CaptureSource? = null,
    val basis: String? = null,
    val ifMatch: String? = null,
    val createOnly: Boolean = false,
)

/** Send the file with `PUT /api/v1/<uploadPath>` and the ticket in the [header] header. Ten minutes, once. */
@Serializable
data class UploadTicketDto(
    val ticket: String,
    val method: String = "PUT",
    val uploadPath: String = "screenshot-uploads",
    val header: String = UPLOAD_TICKET_HEADER,
    val expiresAt: String,
    val sha256: String,
    val byteSize: Int,
)

const val UPLOAD_TICKET_HEADER: String = "Lingvo-Upload-Ticket"
